import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import { getStoreProducts, getStoreCategories, money } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Reveal } from "../components/Motion";

const SORTS = [
  { v: "featured", l: "FEATURED" },
  { v: "newest", l: "NEWEST" },
  { v: "price_asc", l: "PRICE LOW → HIGH" },
  { v: "price_desc", l: "PRICE HIGH → LOW" },
  { v: "az", l: "A–Z" },
];

function ProductCard({ product, index }) {
  const navigate = useNavigate();
  const { add } = useCart();
  const flip = index % 2 === 1;
  const hasVariants = (product.variants || []).length > 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center py-16 md:py-20 border-t border-white/10">
      <div className={`md:col-span-7 ${flip ? "md:order-2" : ""}`}>
        <Reveal y={50}>
          <button
            onClick={() => navigate(`/store/${product.lookupKey}`)}
            className="relative block w-full overflow-hidden aspect-[4/3] group"
            data-testid={`product-image-${product.lookupKey}`}
          >
            <motion.img
              src={product.artwork}
              alt={product.name}
              className="w-full h-full object-cover"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            />
            <div className="absolute inset-0 bg-black/20 group-hover:bg-black/5 transition-colors duration-700" />
            {product.featured && (
              <span className="absolute bottom-5 left-5 font-mono text-[9px] tracking-[0.25em] text-black bg-gold px-3 py-1.5">
                FEATURED
              </span>
            )}
            {product.edition === "Limited Edition" && (
              <span className="absolute top-5 left-5 font-mono text-[9px] tracking-[0.25em] text-gold border border-gold/40 px-3 py-1.5 bg-black/40">
                LIMITED {product.limitedQuantity ? `· ${product.limitedQuantity}` : ""}
              </span>
            )}
            {product.preorder && (
              <span className="absolute top-5 right-5 font-mono text-[9px] tracking-[0.25em] text-white border border-white/40 px-3 py-1.5 bg-black/40">
                PRE-ORDER
              </span>
            )}
          </button>
        </Reveal>
      </div>

      <div className={`md:col-span-5 ${flip ? "md:order-1" : ""}`}>
        <Reveal delay={0.1}>
          <p className="font-mono text-[11px] tracking-[0.3em] text-gold mb-3">
            {product.subtitle?.toUpperCase()} {product.format ? `· ${product.format}` : ""}
          </p>
          <h3 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light leading-[1.02] text-white break-words">{product.name}</h3>
          <p className="text-white/60 leading-relaxed mt-6 max-w-md">{product.description}</p>

          <div className="flex items-center gap-8 mt-8">
            <span className="font-serif text-3xl text-white">{money(product.price, product.currency)}</span>
            {product.dimension && (
              <span className="font-mono text-[10px] tracking-[0.2em] text-white/40">DIMENSION {product.dimension}</span>
            )}
            {!product.available && (
              <span className="font-mono text-[10px] tracking-[0.2em] text-red-400/80">UNAVAILABLE</span>
            )}
          </div>

          <div className="flex items-center gap-4 mt-8">
            <button
              onClick={() => (hasVariants ? navigate(`/store/${product.lookupKey}`) : add(product))}
              disabled={!product.available}
              data-testid={`add-to-cart-${product.lookupKey}`}
              className="bg-gold text-black font-mono text-[11px] tracking-[0.25em] px-8 py-3.5 hover:bg-white transition-colors duration-300 disabled:opacity-40"
            >
              {hasVariants ? "CHOOSE OPTIONS" : "ADD TO CART"}
            </button>
            <button
              onClick={() => navigate(`/store/${product.lookupKey}`)}
              data-testid={`view-product-${product.lookupKey}`}
              className="border border-white/30 font-mono text-[11px] tracking-[0.25em] px-8 py-3.5 hover:border-gold hover:text-gold transition-colors duration-300"
            >
              DETAILS
            </button>
          </div>
        </Reveal>
      </div>
    </div>
  );
}

export default function Store() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("featured");
  const [availOnly, setAvailOnly] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    document.title = "Store — Motion & Meaning";
    getStoreCategories().then(setCats).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      getStoreProducts({
        category: cat || undefined,
        q: q || undefined,
        sort,
        availability: availOnly ? "available" : undefined,
      })
        .then(setProducts)
        .catch(() => {})
        .finally(() => setLoaded(true));
    }, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [cat, q, sort, availOnly]);

  return (
    <div className="pt-32 md:pt-44 pb-32" data-testid="store-page">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <Reveal>
          <p className="font-mono text-[11px] tracking-[0.4em] text-gold mb-6">OBJECTS OF THE UNIVERSE</p>
          <h1 className="font-serif text-6xl sm:text-7xl lg:text-9xl font-light leading-none text-white">STORE</h1>
        </Reveal>

        {/* Controls */}
        <div className="mt-14 space-y-5" data-testid="store-controls">
          <div className="flex flex-wrap items-center gap-3">
            <button onClick={() => setCat("")} data-testid="store-cat-all"
              className={`font-mono text-[10px] tracking-[0.2em] border px-5 py-2.5 transition-colors ${
                !cat ? "border-gold text-gold bg-gold/10" : "border-white/15 text-white/45 hover:border-white/40"}`}>
              ALL
            </button>
            {cats.map((c) => (
              <button key={c.id} onClick={() => setCat(cat === c.slug ? "" : c.slug)} data-testid={`store-cat-${c.slug}`}
                className={`font-mono text-[10px] tracking-[0.2em] border px-5 py-2.5 transition-colors ${
                  cat === c.slug ? "border-gold text-gold bg-gold/10" : "border-white/15 text-white/45 hover:border-white/40"}`}>
                {c.name.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="store-search"
                placeholder="SEARCH THE STORE…"
                className="w-full bg-transparent border border-white/15 focus:border-gold outline-none pl-11 pr-4 py-3 font-mono text-[11px] tracking-[0.15em] text-white placeholder:text-white/25 transition-colors" />
            </div>
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="store-sort"
              className="bg-[#0a0a0a] border border-white/15 focus:border-gold outline-none px-4 py-3 font-mono text-[10px] tracking-[0.2em] text-white/70">
              {SORTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
            <button onClick={() => setAvailOnly(!availOnly)} data-testid="store-availability"
              className={`font-mono text-[10px] tracking-[0.2em] border px-5 py-3 transition-colors ${
                availOnly ? "border-gold text-gold bg-gold/10" : "border-white/15 text-white/45 hover:border-white/40"}`}>
              AVAILABLE NOW
            </button>
          </div>
        </div>

        <div className="mt-8">
          {cat && (() => {
            const c = cats.find((x) => x.slug === cat);
            if (!c?.artwork) return null;
            return (
              <div className="relative h-40 md:h-56 overflow-hidden rounded-[12px] mb-4" data-testid="store-category-banner">
                <img src={c.artwork} alt={c.name} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/30 to-transparent" />
                <div className="absolute bottom-5 left-5 md:left-8 pr-5">
                  <p className="font-mono text-[10px] tracking-[0.35em] text-gold">{c.name.toUpperCase()}</p>
                  {c.description && <p className="text-white/60 text-sm mt-2 max-w-md">{c.description}</p>}
                </div>
              </div>
            );
          })()}
          {loaded && products.length === 0 && (
            <p className="font-serif text-2xl text-white/40 py-20 text-center" data-testid="store-empty">
              Nothing matches that signal. Adjust your filters.
            </p>
          )}
          {products.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </div>
    </div>
  );
}
